/**
 * Tela /imports/history — histórico de importações (TH-53214 / M2.E7.S2).
 *
 * Lista paginada dos batches do tenant com filtros por status e loja.
 * Cada linha tem um link para `/imports/:batchId` (preview existente do
 * wizard quando o batch ainda está em preview, ou simples view do
 * resultado quando applied/failed/rolled_back).
 *
 * Padrão visual: DataTable header mono + Pagination, idêntico a
 * AreasPage/StoresPage. Status sai como StatusPill colorido conforme
 * o tom da fase do batch.
 *
 * Permissões: mesmo conjunto do POST /imports (importer roles).
 * Backend retorna 403 — aqui só protege com RequireAuth.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { Label } from "@/components/ui/Label";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import {
  useImportsHistory,
  type ImportBatchStatus,
  type ImportBatchSummary,
  type ImportsHistoryFilters,
} from "@/hooks/useImportsHistory";

interface StoreOption {
  id: string;
  name: string;
}

interface PagedStoreResponse {
  data: StoreOption[];
}

async function fetchStores(): Promise<StoreOption[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return ((await res.json()) as PagedStoreResponse).data;
}

const STATUS_OPTIONS: { value: ImportBatchStatus; label: string }[] = [
  { value: "uploaded", label: "Enviado" },
  { value: "parsing", label: "Parseando" },
  { value: "preview", label: "Em preview" },
  { value: "applying", label: "Aplicando" },
  { value: "applied", label: "Aplicado" },
  { value: "failed", label: "Falhou" },
  { value: "rolled_back", label: "Revertido" },
];

const STATUS_LABEL: Record<ImportBatchStatus, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ImportBatchStatus, string>;

const STATUS_TONE: Record<ImportBatchStatus, StatusTone> = {
  uploaded: "muted",
  parsing: "muted",
  preview: "warn",
  applying: "warn",
  applied: "success",
  failed: "destructive",
  rolled_back: "destructive",
};

const PER_PAGE = 20;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  // pt-BR curto: 15/05/2026 14:32
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ImportsHistoryPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ImportBatchStatus | "">("");
  const [storeFilter, setStoreFilter] = useState<string>("");

  const filters: ImportsHistoryFilters = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      status: statusFilter || undefined,
      store_id: storeFilter || undefined,
    }),
    [page, statusFilter, storeFilter],
  );

  const stores = useQuery({
    queryKey: ["stores", "for-imports-history"],
    queryFn: fetchStores,
    staleTime: 60_000,
  });

  const history = useImportsHistory(filters);
  const rows = history.data?.data ?? [];
  const total = history.data?.meta.total ?? 0;

  const columns: DataTableColumn<ImportBatchSummary>[] = [
    {
      key: "created_at",
      header: "Data",
      mono: true,
      width: "160px",
      cell: (b) => formatDateTime(b.created_at),
    },
    {
      key: "store_name",
      header: "Loja",
      cell: (b) => b.store_name ?? "—",
    },
    {
      key: "mapping_name",
      header: "Mapeamento",
      cell: (b) => b.mapping_name ?? "—",
    },
    {
      key: "status",
      header: "Status",
      width: "140px",
      cell: (b) => (
        <StatusPill tone={STATUS_TONE[b.status]}>
          {STATUS_LABEL[b.status]}
        </StatusPill>
      ),
    },
    {
      key: "totals",
      header: "Linhas (ok / total)",
      mono: true,
      align: "right",
      width: "180px",
      cell: (b) => {
        if (b.total_rows == null) return "—";
        const ok = b.ok_rows ?? 0;
        const err = b.error_rows ?? 0;
        return (
          <span className="tabular-nums">
            {ok}
            <span className="text-[var(--color-muted-foreground)]"> / {b.total_rows}</span>
            {err > 0 ? (
              <span className="ml-1 text-[var(--color-destructive)]">
                ({err} erro{err === 1 ? "" : "s"})
              </span>
            ) : null}
          </span>
        );
      },
    },
  ];

  const hasFilters = statusFilter !== "" || storeFilter !== "";

  const navigate = useNavigate();
  const historyActions = (
    <Button variant="primary" onClick={() => void navigate({ to: "/imports" })}>
      Nova importação
    </Button>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / IMPORTAÇÕES / HISTÓRICO"
        title="Histórico de importações"
        actions={historyActions}
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-6">

          <div className="flex flex-wrap items-end gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="status-filter" className="text-xs">
                Status
              </Label>
              <Select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as ImportBatchStatus | "");
                  setPage(1);
                }}
                className="h-9 min-w-[180px]"
              >
                <option value="">Todos</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="store-filter" className="text-xs">
                Loja
              </Label>
              <Select
                id="store-filter"
                value={storeFilter}
                onChange={(e) => {
                  setStoreFilter(e.target.value);
                  setPage(1);
                }}
                className="h-9 min-w-[180px]"
                disabled={stores.isLoading}
              >
                <option value="">Todas</option>
                {(stores.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            {hasFilters ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setStatusFilter("");
                  setStoreFilter("");
                  setPage(1);
                }}
                className="h-9"
              >
                Limpar filtros
              </Button>
            ) : null}
          </div>

          {history.isError ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 p-4 text-sm text-[var(--color-destructive)]"
            >
              Erro ao carregar histórico: {history.error.message}
            </div>
          ) : null}

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(b) => b.id}
            emptyMessage={
              hasFilters
                ? "Nenhuma importação bate com os filtros."
                : "Nenhuma importação ainda. Suba sua primeira planilha em /imports."
            }
          />

          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={total}
            onPageChange={setPage}
          />
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
